import { MouseEvent, PropsWithChildren, ReactNode, useCallback } from 'react';

import { Box } from '@mui/material';
import clsx from 'clsx';

import { DatePickerTrigger } from './DatePickerTrigger';
import { SxProps } from '../../../../types';
import { InputProps } from '../../../controls';
import { CalendarBlank } from '../../../icons';
import { useDatePickerContext } from '../useDatePickerContext';

export type DatePickerInputProps = {
  inputProps?: Pick<
    InputProps,
    | 'size'
    | 'sx'
    | 'startAdornment'
    | 'label'
    | 'labelTooltipProps'
    | 'readOnly'
  >;
  testId?: string;
  popperContent: ReactNode;
  sx?: SxProps;
  readOnly?: boolean;
};

export const DatePickerInput = ({
  inputProps,
  testId = 'date-picker',
  popperContent,
  sx,
}: PropsWithChildren<DatePickerInputProps>) => {
  const ctx = useDatePickerContext();

  const isOpen = Boolean(ctx.popoverAnchor);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLInputElement>) => {
      if (!inputProps?.readOnly) {
        ctx.handleOpen(e);
      }
    },
    [ctx, inputProps?.readOnly]
  );

  const startAdornment = inputProps?.startAdornment ?? (
    <CalendarBlank size={20} />
  );

  return (
    <Box sx={sx}>
      <DatePickerTrigger
        onClick={handleClick}
        isOpen={isOpen}
        inputProps={{
          size: 'large',
          focused: isOpen,
          value: ctx.inputValue(),
          ...inputProps,
          startAdornment,
          className: clsx(
            inputProps?.readOnly && 'DS-DatePickerInput-readOnly'
          ),
          // This property is hardcoded, since input is only displaying selected date
          readOnly: true,
        }}
        testId={testId}
      />
      {popperContent}
    </Box>
  );
};
